import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EditorHeader } from "./EditorHeader";
import { EditorLoading } from "./shared/EditorLoading";
import { Loader2, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ALL_TABLES = [
  "business_summary", "core_values", "team_members", "organizational_structure",
  "product_features", "roadmap_items", "stakeholder_interfaces",
  "market_data", "traction_metrics", "investors",
  "financial_metrics", "financial_projections", "revenue_data",
  "monthly_revenue_data", "burn_rate_data", "investment_data", "ahmed_investment_data",
  "pipeline_items", "cap_table_entries", "fund_allocation", "fund_breakdown_items",
  "risk_items", "legal_sections",
];

type Config = {
  id?: string;
  enabled: boolean;
  system_prompt_extra: string;
  refusal_topics: string[];
  allowed_tables: string[];
};

const EMPTY: Config = {
  enabled: true,
  system_prompt_extra: "",
  refusal_topics: [],
  allowed_tables: ALL_TABLES,
};

type TestResult = {
  reply: string;
  system_prompt: string;
  system_prompt_extra: string;
  initial_reply: string;
  adherence_passed: boolean;
  adherence_reason: string;
  was_corrected: boolean;
};

export const AIAssistantEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingExtra, setSavingExtra] = useState(false);
  const [lastSavedExtra, setLastSavedExtra] = useState("");
  const [config, setConfig] = useState<Config>(EMPTY);
  const [topicDraft, setTopicDraft] = useState("");

  // Prompt test panel
  const [testQuestion, setTestQuestion] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const extraDirty = config.system_prompt_extra !== lastSavedExtra;

  const saveExtraOnly = async () => {
    setSavingExtra(true);
    const value = config.system_prompt_extra;
    const updated_at = new Date().toISOString();
    try {
      if (config.id) {
        const { error } = await supabase
          .from("ai_assistant_config")
          .update({ system_prompt_extra: value, updated_at })
          .eq("id", config.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("ai_assistant_config")
          .insert({
            enabled: config.enabled,
            system_prompt_extra: value,
            refusal_topics: config.refusal_topics,
            allowed_tables: config.allowed_tables,
            updated_at,
          })
          .select("id")
          .single();
        if (error) throw error;
        if (data?.id) setConfig(c => ({ ...c, id: data.id as string }));
      }
      setLastSavedExtra(value);
      toast.success("Instructions saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSavingExtra(false);
    }
  };

  const runTest = async () => {
    const q = testQuestion.trim();
    if (!q) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("investor-chat", {
        body: { message: q, test_mode: true },
      });
      if (error) throw error;
      const reply = (data as { reply?: string })?.reply ?? "";
      const debug = (data as { debug?: Omit<TestResult, "reply"> })?.debug;
      if (!debug) throw new Error("Missing debug payload");
      setTestResult({ reply, ...debug });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Test failed");
    } finally {
      setTesting(false);
    }
  };


  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from("ai_assistant_config")
        .select("*")
        .limit(1)
        .maybeSingle();
      if (error) toast.error("Failed to load config");
      if (data) {
        setConfig({
          id: data.id,
          enabled: data.enabled,
          system_prompt_extra: data.system_prompt_extra ?? "",
          refusal_topics: data.refusal_topics ?? [],
          allowed_tables: data.allowed_tables ?? ALL_TABLES,
        });
        setLastSavedExtra(data.system_prompt_extra ?? "");
      }
      setLoading(false);
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    const payload = {
      enabled: config.enabled,
      system_prompt_extra: config.system_prompt_extra,
      refusal_topics: config.refusal_topics,
      allowed_tables: config.allowed_tables,
      updated_at: new Date().toISOString(),
    };
    const query = config.id
      ? supabase.from("ai_assistant_config").update(payload).eq("id", config.id)
      : supabase.from("ai_assistant_config").insert(payload);
    const { error } = await query;
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    setLastSavedExtra(config.system_prompt_extra);
    toast.success("Saved");
  };

  const toggleTable = (t: string, on: boolean) => {
    setConfig(c => ({
      ...c,
      allowed_tables: on ? Array.from(new Set([...c.allowed_tables, t])) : c.allowed_tables.filter(x => x !== t),
    }));
  };

  const addTopic = () => {
    const v = topicDraft.trim();
    if (!v) return;
    setConfig(c => ({ ...c, refusal_topics: Array.from(new Set([...c.refusal_topics, v])) }));
    setTopicDraft("");
  };

  if (loading) return <EditorLoading />;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <EditorHeader title="Investor Assistant Settings" />

      <Card>
        <CardHeader>
          <CardTitle>Kill switch</CardTitle>
          <CardDescription>When off, the chat button is hidden and the assistant refuses to reply.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Switch checked={config.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
          <Label>{config.enabled ? "Enabled" : "Disabled"}</Label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Extra system instructions</CardTitle>
          <CardDescription>Appended to the system prompt with highest priority. Useful for tone, constraints, or company-specific rules.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={6}
            value={config.system_prompt_extra}
            onChange={e => setConfig(c => ({ ...c, system_prompt_extra: e.target.value }))}
            placeholder="e.g. Never speculate on next-round valuation. Always recommend the user speak with the founder for legal questions."
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {extraDirty
                ? "Unsaved changes — save before running a prompt test."
                : "Saved. Prompt tests will use these instructions."}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={saveExtraOnly}
              disabled={savingExtra || !extraDirty}
            >
              {savingExtra ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : "Save instructions"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Refusal topics</CardTitle>
          <CardDescription>The assistant will politely refuse any question matching these topics.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={topicDraft}
              onChange={e => setTopicDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
              placeholder="e.g. valuation speculation"
            />
            <Button type="button" onClick={addTopic}>Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {config.refusal_topics.length === 0 && (
              <span className="text-sm text-muted-foreground">No refusal topics set.</span>
            )}
            {config.refusal_topics.map(t => (
              <Badge key={t} variant="secondary" className="gap-1">
                {t}
                <button
                  onClick={() => setConfig(c => ({ ...c, refusal_topics: c.refusal_topics.filter(x => x !== t) }))}
                  className="ml-1 hover:text-destructive"
                  aria-label={`Remove ${t}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allowed data tables</CardTitle>
          <CardDescription>Only the checked tables are loaded into the assistant's context. Uncheck a table to hide it from the AI entirely.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ALL_TABLES.map(t => {
              const on = config.allowed_tables.includes(t);
              return (
                <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={on} onCheckedChange={v => toggleTable(t, !!v)} />
                  <span className="font-mono text-xs">{t}</span>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prompt test</CardTitle>
          <CardDescription>
            Run a sample question against the currently SAVED config to confirm the Extra System Instructions are being applied.
            This call does not affect investor chat history.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={3}
            value={testQuestion}
            onChange={(e) => setTestQuestion(e.target.value)}
            placeholder="e.g. What is the burn rate and runway?"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {extraDirty
                ? "Save your Extra system instructions first — tests use the saved version."
                : "Tests run against the last saved Extra system instructions."}
            </span>
            <Button onClick={runTest} disabled={testing || !testQuestion.trim() || extraDirty}>
              {testing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Running…</> : "Run test"}
            </Button>
          </div>

          {testResult && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2 text-sm">
                {testResult.adherence_passed ? (
                  <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" />Adherence passed</Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Violation detected</Badge>
                )}
                {testResult.was_corrected && (
                  <Badge variant="outline">Auto-corrected</Badge>
                )}
              </div>
              {testResult.adherence_reason && (
                <p className="text-xs text-muted-foreground">Reason: {testResult.adherence_reason}</p>
              )}

              <div>
                <Label className="text-xs uppercase text-muted-foreground">Final reply</Label>
                <div className="mt-1 rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">{testResult.reply}</div>
              </div>

              {testResult.was_corrected && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-xs uppercase text-muted-foreground">Initial (pre-correction) reply</summary>
                  <div className="mt-1 rounded-md border bg-muted/20 p-3 whitespace-pre-wrap">{testResult.initial_reply}</div>
                </details>
              )}

              <details>
                <summary className="cursor-pointer text-xs uppercase text-muted-foreground">Assembled system prompt</summary>
                <pre className="mt-1 max-h-80 overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap font-mono">{testResult.system_prompt}</pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} size="lg">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default AIAssistantEditor;
